import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bot, Clock, Zap, Info, ShieldAlert, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminAutopilot() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/autopilot/settings`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось загрузить настройки");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (payload) => {
    setSaving(true); setError(""); setMsg("");
    try {
      const r = await axios.patch(`${API}/admin/autopilot/settings`, payload, { withCredentials: true });
      setData((d) => ({ ...d, settings: r.data.settings }));
      setMsg("Настройки сохранены");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const toggleManager = async (mgr, val) => {
    try {
      await axios.patch(`${API}/admin/autopilot/manager/${mgr.id}`,
                          { enabled: val }, { withCredentials: true });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Ошибка");
    }
  };

  const runNow = async () => {
    setTesting(true); setError(""); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/autopilot/run-now`, {}, { withCredentials: true });
      setMsg(`Тестовый запуск: отправлено ${r.data.replies_sent} сообщений`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка запуска");
    } finally {
      setTesting(false);
    }
  };

  if (!data) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontFamily: "Manrope" }}>
      {error || "Загрузка…"}
    </div>;
  }

  const s = data.settings;
  const canEditMaster = data.role === "admin" || data.role === "senior_manager";

  return (
    <div data-testid="admin-autopilot" style={{ paddingBottom: 32 }}>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Bot size={26} color="#C5A059" />
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#111", margin: 0 }}>
            Autopilot — AI-автоответчик
          </h2>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", maxWidth: 720, lineHeight: 1.6 }}>
          Когда клиент пишет в WhatsApp и никто из менеджеров не отвечает за указанное время —
          Autopilot автоматически формирует ответ от имени ответственного менеджера,
          используя <b>Базу знаний</b> и стиль бутика. Каждое AI-сообщение помечается бейджем
          <span style={badgeAiInline}>AI</span> в переписке.
        </div>
      </div>

      {error && <Banner tone="error" text={error} />}
      {msg && <Banner tone="success" text={msg} />}

      {/* Master toggle */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Zap size={16} color="#C5A059" />
              <div style={cardTitle}>Главный выключатель</div>
            </div>
            <div style={cardSub}>
              Общий рубильник Autopilot. Даже если у отдельных менеджеров он включён —
              без этого переключателя AI не отправит ни одного сообщения.
            </div>
          </div>
          <Toggle
            testId="autopilot-master-toggle"
            checked={s.master_enabled}
            onChange={(v) => patch({ master_enabled: v })}
            disabled={saving || !canEditMaster}
          />
        </div>
      </Card>

      {/* Time window */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Clock size={16} color="#C5A059" />
          <div style={cardTitle}>Расписание работы</div>
        </div>
        <div style={cardSub}>
          В это время Autopilot будет отвечать за менеджеров. По умолчанию — ночь и раннее утро
          (с 19:00 до 10:00). Если поставите одинаковое время — Autopilot работает 24/7.
          Часовой пояс: <b>Asia/Almaty</b>.
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <TimeField
            label="Начало"
            value={s.window_start}
            testId="autopilot-window-start"
            disabled={saving || !canEditMaster}
            onSave={(v) => patch({ window_start: v })}
          />
          <div style={{ color: "#94a3b8", fontFamily: "Manrope", fontSize: 20, marginBottom: 6 }}>→</div>
          <TimeField
            label="Конец"
            value={s.window_end}
            testId="autopilot-window-end"
            disabled={saving || !canEditMaster}
            onSave={(v) => patch({ window_end: v })}
          />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-end", gap: 10 }}>
            <NumberField
              label="Задержка ответа (мин.)"
              value={s.reply_delay_minutes}
              testId="autopilot-delay"
              disabled={saving || !canEditMaster}
              onSave={(v) => patch({ reply_delay_minutes: v })}
              min={0} max={60}
            />
          </div>
        </div>
      </Card>

      {/* Per-manager toggles */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <ShieldAlert size={16} color="#C5A059" />
          <div style={cardTitle}>Персональные настройки менеджеров</div>
        </div>
        <div style={cardSub}>
          Для каждого менеджера — свой переключатель. AI отвечает за клиента <b>только если у
          назначенного онлайн-менеджера включён Autopilot</b>. Если клиент свободен (без
          менеджера), AI отвечает под именем дежурного или админа с включённым Autopilot.
        </div>
        <div style={{ marginTop: 14, background: "#fafafa", borderRadius: 8, border: "1px solid #eee" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: 13 }}>
            <thead style={{ background: "#f5f5f4" }}>
              <tr>
                <th style={th}>Менеджер</th>
                <th style={th}>Email</th>
                <th style={th}>Роль</th>
                <th style={{ ...th, textAlign: "right" }}>Autopilot</th>
              </tr>
            </thead>
            <tbody>
              {(data.managers || []).map((m) => (
                <tr key={m.id} data-testid={`autopilot-mgr-row-${m.id}`}
                  style={{ borderTop: "1px solid #eee" }}>
                  <td style={td}>{m.name}</td>
                  <td style={{ ...td, color: "#64748b" }}>{m.email}</td>
                  <td style={{ ...td, color: "#64748b" }}>{m.role}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <Toggle
                      testId={`autopilot-mgr-toggle-${m.id}`}
                      checked={m.autopilot_enabled}
                      onChange={(v) => toggleManager(m, v)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Test run */}
      {canEditMaster && (
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Info size={16} color="#C5A059" />
            <div style={cardTitle}>Ручной запуск</div>
          </div>
          <div style={cardSub}>
            Обычно Autopilot проверяет чаты раз в минуту. Нажмите кнопку ниже, чтобы
            запустить проверку прямо сейчас — удобно для теста настроек.
          </div>
          <div style={{ marginTop: 14 }}>
            <button data-testid="autopilot-run-now" onClick={runNow} disabled={testing}
              style={{
                background: testing ? "#e5e7eb" : "linear-gradient(135deg,#C5A059,#D4B878)",
                color: "#0A0A0A", border: "none", borderRadius: 6,
                padding: "10px 18px", cursor: testing ? "wait" : "pointer",
                fontFamily: "Manrope", fontWeight: 700, fontSize: 13,
                display: "inline-flex", alignItems: "center", gap: 8,
              }}>
              <RefreshCw size={14} className={testing ? "animate-spin" : ""} />
              {testing ? "Проверяем…" : "Запустить проверку сейчас"}
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}

/* --------------------------- UI atoms --------------------------- */

function Card({ children }) {
  return (
    <div style={{
      background: "white", border: "1px solid #e5e7eb", borderRadius: 10,
      padding: 20, marginBottom: 16,
    }}>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button data-testid={testId} onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        width: 46, height: 26, borderRadius: 999,
        border: "none", padding: 3,
        background: checked ? "linear-gradient(135deg,#C5A059,#D4B878)" : "#e5e7eb",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 0.2s",
        opacity: disabled ? 0.5 : 1,
        position: "relative",
      }}>
      <div style={{
        width: 20, height: 20, borderRadius: "50%",
        background: "white", transition: "transform 0.2s",
        transform: checked ? "translateX(20px)" : "translateX(0)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }} />
    </button>
  );
}

function TimeField({ label, value, onSave, disabled, testId }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <label style={{ display: "flex", flexDirection: "column", fontFamily: "Manrope", fontSize: 11, color: "#64748b", gap: 4 }}>
      {label}
      <input data-testid={testId} type="time" value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== value && onSave(v)}
        disabled={disabled}
        style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px",
                  fontFamily: "monospace", fontSize: 14, width: 130 }} />
    </label>
  );
}

function NumberField({ label, value, onSave, disabled, min, max, testId }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <label style={{ display: "flex", flexDirection: "column", fontFamily: "Manrope", fontSize: 11, color: "#64748b", gap: 4 }}>
      {label}
      <input data-testid={testId} type="number" value={v} min={min} max={max}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => Number(v) !== Number(value) && onSave(Number(v))}
        disabled={disabled}
        style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px",
                  fontSize: 14, width: 140 }} />
    </label>
  );
}

function Banner({ tone, text }) {
  const styles = tone === "error"
    ? { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" }
    : { background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" };
  return (
    <div data-testid={`autopilot-banner-${tone}`}
      style={{ ...styles, padding: "10px 14px", borderRadius: 8, marginBottom: 14,
                fontFamily: "Manrope", fontSize: 13 }}>
      {text}
    </div>
  );
}

/* --------------------------- style tokens --------------------------- */
const cardTitle = { fontFamily: "Manrope", fontSize: 15, fontWeight: 700, color: "#0A0A0A" };
const cardSub = { fontFamily: "Manrope", fontSize: 12.5, color: "#64748b", marginTop: 4, lineHeight: 1.6 };
const th = { textAlign: "left", padding: "10px 14px", color: "#374151", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "12px 14px", color: "#111", verticalAlign: "middle" };
const badgeAiInline = {
  display: "inline-block", background: "linear-gradient(135deg,#C5A059,#D4B878)",
  color: "#0A0A0A", padding: "1px 6px", borderRadius: 4, fontSize: 10,
  fontWeight: 800, marginLeft: 4, marginRight: 2, letterSpacing: 0.5, verticalAlign: "middle",
};
